import { motion } from "framer-motion";
import { EmailTemplates } from "@/components/account/EmailTemplates";
import { LineItemTemplates } from "@/components/account/LineItemTemplates";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Define the location state type for TypeScript safety
interface LocationState {
  expandAccordion?: string;
}

const Templates = () => {
  const { t, i18n, ready } = useTranslation();
  const [expandedAccordion, setExpandedAccordion] = useState<string | undefined>(undefined);
  const location = useLocation();
  const [isReady, setIsReady] = useState(false);

  // Extract state from router location
  const locationState = location.state as LocationState;

  useEffect(() => {
    // Wait for translations to be ready
    if (ready) {
      setIsReady(true);

      // Set expanded accordion based on router state
      if (locationState?.expandAccordion) {
        setExpandedAccordion(locationState.expandAccordion);
      } else {
        // Fallback to localStorage for backward compatibility
        const accordionToExpand = localStorage.getItem("expandAccordionSection");
        if (accordionToExpand) {
          setExpandedAccordion(accordionToExpand);
          // Clear the localStorage value after using it
          localStorage.removeItem("expandAccordionSection");
        }
      }
    }
  }, [ready, locationState, i18n.language, t]);

  if (!isReady) {
    return (
      <div className="container mx-auto py-4 space-y-6">
        <div className="animate-pulse">
          <div className="h-10 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-4 space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full"
      >
        <h1 className="font-archivoblack text-4xl md:text-5xl font-bold mb-4 md:mb-6 text-left text-[#34375B]">
          {t("templates.title", "Templates")}
        </h1>

        <Accordion
          type="single"
          collapsible
          className="w-full space-y-4"
          value={expandedAccordion}
          onValueChange={setExpandedAccordion}
          defaultValue={expandedAccordion}
        >
          <AccordionItem value="email-templates" className="border rounded-lg px-4 bg-[#8791F3] text-white">
            <AccordionTrigger className="py-4 hover:no-underline">
              <span className="text-xl font-semibold">{t("templates.email_templates", "Email Templates")}</span>
            </AccordionTrigger>
            <AccordionContent className="pt-2 pb-4">
              <EmailTemplates />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="line-item-templates" className="border rounded-lg px-4 bg-[#8791F3] text-white">
            <AccordionTrigger className="py-4 hover:no-underline">
              <span className="text-xl font-semibold">{t("templates.line_item_templates", "Line Item Templates")}</span>
            </AccordionTrigger>
            <AccordionContent className="pt-2 pb-4">
              <LineItemTemplates />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </motion.div>
    </div>
  );
};

export default Templates;
